import { gql } from '__generated__/gql';
import { ApolloCache } from '@apollo/client';

export const LOG_EMAILHISTORY_SHORT_FRAGMENT = gql(`
  fragment EmailHistoryOutputShort on EmailHistoryOutput {
    id
    createdAt
    hotelRef
    offerRef
    offer { id refCode }
    offerVersionRef
    offerVersion { id version }
    userName
    templateName
    templateVariables
    
    # Enhanced fields (primary)
    fromAddress
    replyToAddress
    sentAt
    body
    
    # Enhanced relations
    recipients {
      id
      emailAddress
      recipientType
      source
      sourceName
      hotelContactId
      clientId
      userId
      deliveryStatus
      deliveryError
      deliveryStatusAt
    }
    
    attachments {
      id
      filename
      attachmentType
      offerVersionId
      eventId
      mimeType
    }
    
    subject
    err
    success
    space { ...SpaceShort }
  }
`);

export const HOTEL_VALIDATION_SUMMARY_FRAGMENT = gql(`
  fragment HotelValidationSummary on HotelPricelistValidationSummary {
    productsWithoutBasePrice
    productsWithoutBasePriceNames
    productsWithoutPriceComponents
    productsWithoutPriceComponentsNames
    bundlesWithoutBasePrice
    bundlesWithoutBasePriceNames
    bundlesWithoutPriceComponents
    bundlesWithoutPriceComponentsNames
    facilitiesWithoutBasePrice
    facilitiesWithoutBasePriceNames
    facilitiesWithoutPriceComponents
    facilitiesWithoutPriceComponentsNames
    facilitiesWithoutTaxRates
    facilitiesWithoutTaxRatesNames
    productsWithoutTaxRates
    productsWithoutTaxRatesNames
    bundlesWithoutTaxRates
    bundlesWithoutTaxRatesNames
    serviceTypesIncomplete {
      serviceTypeName
      missingProducts
      missingFacilities
      invalidProducts
      invalidFacilities
    }
  }
`);

export const HOTEL_LIST_SHORT_FRAGMENT = gql(`
  fragment HotelListShort on HotelListOutput {
    id
    name
    refCode
    slug
    extRefCode
    businessCountry
    widgetTestUrl
    widgetStatus
    listerUrl
    listerStatus
    listerValidation { ...HotelValidationSummary }
    widgetValidation { ...HotelValidationSummary }
    space { ...SpaceShort }
    integrations { id secret { id name system } }
  }
`);

export const HOTEL_LIST_MINIMAL_FRAGMENT = gql(`
  fragment HotelListMinimal on HotelListOutput {
    id
    name
    refCode
    slug
    extRefCode
    businessCountry
    widgetTestUrl
    widgetStatus
    listerUrl
    wordpressUrl
    listerStatus
    space { ...SpaceShort }
    integrations { id secret { id name system } }
  }
`);

export const HOTEL_CONTACT_FRAGMENT = gql(`
  fragment HotelContactFull on HotelContactOutput {
    id
    firstname
    lastname
    title
    job
    gender
    phone
    email
    contactType
  }
`);

export const HOTEL_LIST_DROPDOWN_FRAGMENT = gql(`
  fragment HotelListDropdown on HotelListOutput {
    id
    name
    slug
    refCode
    extRefCode
    businessCountry
    space { ...SpaceShort }
    serviceTypes { forLister forWidget serviceType { id name sku } }
  }
`);

export const HOTEL_LIST_VIEW_FRAGMENT = gql(`
  fragment HotelListView on HotelListOutput {
    id
    name
    slug
    refCode
    extRefCode
    rating
    billingBic
    billingIban
    billingUid
    businessName
    businessAddress1
    businessAddress2
    businessAddress3
    businessAddress4
    businessCountry
    businessZip
    businessCity
    businessState
    businessEmail
    businessUrl
    businessPhone
    invoiceAddress {
      address1
      address2
      address3
      address4
      city
      country
      state
      zip
    }
    notificationEmail
    reminders {
      template
      receivers
    }
    widgetUrl
    widgetColor
    widgetColorEnabled
    widgetPrivacyUrl
    widgetTermsUrl
    widgetNoRooms
    widgetExpandedMode
    widgetSlimMode
    widgetTestUrl
    widgetStatus
    widgetBaseUrl
    widgetValidation { ...HotelValidationSummary }
    listerUrl
    listerStatus
    listerValidation { ...HotelValidationSummary }
    offerNotApprovedReminderHotel
    offerNotApprovedReminderClient
    depositReminderHotel
    depositReminderCheckHotel
    enableAvailabilityCheck
    offerExpirationDays
    locationLatitude
    locationLongitude
    showInLister
    showInWidget
    medias { id name previewUrl description logo offer outdoorView restaurant room seminarRoom homepage }
    meetingRooms {
      id
      name
      area
      capacityUForm
      capacityTheater
      capacityParlament
      capacityCircle
      capacityBankett
      capacityCocktail
      capacityBlock
      daylight
      airConditioning
      facilityId
    }
    maxCapacityRooms
    maxCapacityPeople
    freeParkingSpaces
    paidParkingSpaces
    attributes { id attribute }
    cancellationToleranceRate
    cancellationRules {
      id
      daysToEvent
      minCapacity
      maxCapacity
      minOvernight
      maxOvernight
      minTotalGuests
      maxTotalGuests
      toleranceRate
      rate
    }
    texts {
      id
      details
      language
      type
    }
    contacts {
      ...HotelContactFull
    }
    offerTemplate { id name }
    space { ...SpaceShort }
    integrations { id secret { id name system } }
    serviceTypes { forLister forWidget listerServiceTypeAssignment serviceType { id name sku } }
    hasActivePartnerContract
    groups { id name }
    contracts {
      id
      type
      isActive
      validFrom
      validTo
    }
    agb {
      id
      createdAt
      updatedAt
      agbFile {
        id
        originalFilename
        storedFilename
        fileSize
        mimeType
        createdAt
        updatedAt
      }
      uploader {
        id
        name
        email
      }
    }
    documents {
      id
      hotelId
      documentFileId
      displayName
      approved
      approvedAt
      approvedBy
      createdAt
      uploadedBy
      documentFile {
        id
        originalFilename
        fileSize
        mimeType
      }
      uploader {
        id
        name
      }
      approver {
        id
        name
      }
    }
  }
`);

export const GET_WORDPRESS_URL_QUERY = gql(`
  query GetWordpressUrl($slug: String!) {
    getWordpressUrl(slug: $slug)
  }
`);

export const HOTEL_GROUPS_LIST_QUERY = gql(`
  query ListHotelGroupsForAssignment($spaceId: Int) {
    listHotelGroups(spaceId: $spaceId) {
      totalCount
      hotelGroups {
        id
        name
      }
    }
  }
`);

export const COORDINATES_BY_ADDRESS_QUERY = gql(`
  query CoordinatesByAddress($address: String!) {
    coordinatesByAddress(address: $address) {
      lat
      lng
    }
  }
`);

export const HOTEL_LIST_QUERY = gql(`
  query ListHotels($spaceId: Int, $hotelId: Int, $skip: Int, $take: Int, $search: String) {
    listHotels(spaceId: $spaceId, hotelId: $hotelId, skip: $skip, take: $take, search: $search) {
      totalCount
      hotels {
        ...HotelListMinimal
      }
    }
  }
`);

export const HOTEL_VALIDATION_QUERY = gql(`
  query HotelValidation($hotelId: Int!) {
    listHotels(hotelId: $hotelId, skip: 0, take: 1) {
      hotels {
        id
        listerValidation { ...HotelValidationSummary }
        widgetValidation { ...HotelValidationSummary }
      }
    }
  }
`);

export const HOTEL_LIST_STATUS_QUERY = gql(`
  query ListHotelsStatus($spaceId: Int) {
    listHotels(spaceId: $spaceId, skip: 0, take: 100) @connection(key: "listHotelsStatus", filter: ["spaceId"]) {
      hotels {
        id
        name
        refCode
        widgetStatus
        listerStatus
      }
    }
  }
`);

export const HOTEL_LIST_DROPDOWN_QUERY = gql(`
  query ListHotelsDropdown($spaceId: Int) {
    listHotels(spaceId: $spaceId, skip: 0, take: 100) @connection(key: "listHotelsDropdown", filter: ["spaceId"]) {
      hotels {
        ...HotelListDropdown
      }
    }
  }
`);

export const HOTEL_VIEW_QUERY = gql(`
  query ViewHotel($id: Int!) {
    viewHotel(id: $id) {
      ...HotelListView
    }
  }
`);

export const CREATE_HOTEL_MUTATION = gql(`
  mutation CreateHotel($spaceId: Int!, $data: HotelCreateInput!) {
    createHotel(spaceId: $spaceId, data: $data) {
      ...HotelListView
    }
  }
`);

export const UPDATE_HOTEL_MUTATION = gql(`
  mutation UpdateHotel($id: Int!, $data: HotelInput!) {
    updateHotel(id: $id, data: $data) {
      ...HotelListView
    }
  }
`);

export const UPDATE_HOTEL_GROUPS_MUTATION = gql(`
  mutation UpdateHotelGroups($id: Int!, $data: HotelInput!) {
    updateHotel(id: $id, data: $data) {
      id
      groups { id name }
    }
  }
`);

export const DELETE_HOTEL_MUTATION = gql(`
  mutation DeleteHotel($id: Int!) {
    deleteHotel(id: $id)
  }
`);

export const UPLOAD_HOTEL_AGB_MUTATION = gql(`
  mutation UploadHotelAGB($data: HotelAGBInput!) {
    uploadHotelAGB(data: $data) {
      id
      createdAt
      updatedAt
      agbFile {
        id
        originalFilename
        storedFilename
        fileSize
        mimeType
        createdAt
        updatedAt
      }
      uploader {
        id
        name
        email
      }
    }
  }
`);

export const UPLOAD_HOTEL_DOCUMENT_MUTATION = gql(`
  mutation UploadHotelDocument($data: HotelDocumentInput!) {
    uploadHotelDocument(data: $data) {
      id
      displayName
      approved
      approvedAt
      approvedBy
      createdAt
      uploadedBy
      documentFile {
        id
        originalFilename
        fileSize
        mimeType
      }
      uploader { id name }
      approver { id name }
    }
  }
`);

export const DELETE_HOTEL_DOCUMENT_MUTATION = gql(`
  mutation DeleteHotelDocument($id: Int!) {
    deleteHotelDocument(id: $id)
  }
`);

export const APPROVE_HOTEL_DOCUMENT_MUTATION = gql(`
  mutation ApproveHotelDocument($id: Int!) {
    approveHotelDocument(id: $id) {
      id
      approved
      approvedAt
      approvedBy
      approver { id name }
    }
  }
`);

export const EVICT_HOTELS_QUERIES = (cache: ApolloCache<any>) => {
  cache.evict({ fieldName: 'listHotels' });
  cache.gc();
};

export const REFETCH_HOTELS_QUERIES = (hotelId?: number | undefined) => [
  ...(hotelId
    ? [
        {
          query: HOTEL_VIEW_QUERY,
          variables: { id: hotelId },
        },
      ]
    : []),
];

export const SUDO_MUTATION = gql(`
  mutation SudoMutation($id: Int!) {
    sudo(id: $id) {
      success
      user { 
        ...UserFull
      }
    }
  }
`);

export const SEM_PLAN_FRAGMENT = gql(`
  fragment SEMPlanFull on SEMPlan {
    id
    plan
    label
    comment
    flags {
      maxUsers
      maxSeminarRooms
      maxOfferTemplates
      multiTenancy
      adFree
    }
  }
`);

export const USER_FULL_FRAGMENT = gql(`
  fragment UserFull on User {
    id
    createdAt
    updatedAt
    name
    username
    email
    isAdmin
    isLocked
    hasLister
    hasWidget
    lastSeen
    space {
      id
      name
      country
      activeSEMPlan { id semPlan { ...SEMPlanFull } }
    }
    memberships {
      isAdmin
      space { ...SpaceShort }
    }
  }
`);

export const USERS_LIST_QUERY = gql(`
  query ListUsers($spaceId: Int, $skip: Int, $take: Int, $search: String) {
    listUsers(spaceId: $spaceId, skip: $skip, take: $take, search: $search) {
      totalCount
      users {
        ...UserFull
      }
    }
  }
`);

export const USER_VIEW_QUERY = gql(`
  query ViewUser($id: Int!) {
    viewUser(id: $id) {
      ...UserFull
    }
  }
`);

export const CREATE_USER_MUTATION = gql(`
  mutation CreateUser($spaceId: Int, $data: UserCreateInput!) {
    createUser(spaceId: $spaceId, data: $data) {
      ...UserFull
    }
  }
`);

export const UPDATE_USER_MUTATION = gql(`
  mutation UpdateUser($id: Int!, $data: UserUpdateInput!) {
    updateUser(id: $id, data: $data) {
      ...UserFull
    }
  }
`);

export const RESET_USERPASSWORD_MUTATION = gql(`
  mutation ResetUserPassword($id: Int!) {
    resetUserPassword(id: $id)
  }
`);

export const SET_USERPASSWORD_MUTATION = gql(`
  mutation SetUserPassword($id: Int!, $newPassword: String!) {
    setUserPassword(id: $id, newPassword: $newPassword)
  }
`);

export const CHANGE_USERPASSWORD_MUTATION = gql(`
  mutation ChangeUserPassword($id: Int, $password: String, $passwordResetCode: String, $newPassword: String!) {
    changeUserPassword(id: $id, password: $password, passwordResetCode: $passwordResetCode, newPassword: $newPassword)
  }
`);

export const EVICT_USERS_QUERIES = (cache: ApolloCache<any>) => {
  cache.evict({ fieldName: 'listUsers' });
  cache.evict({ fieldName: 'listSpaces' });
  cache.gc();
};

export const REFETCH_USERS_QUERIES = (userId?: number | undefined) => [];

export const SPACE_FULL_FRAGMENT = gql(`
  fragment SpaceFull on Space {
    id
    createdAt
    updatedAt
    name
    country
    hasEpilo
    users { id name email }
    members { isAdmin user { id name email } }
    parent { id name country }
    children { id name country }
    activeSEMPlan { id validFrom validTo semPlan { id plan label } }
  }
`);

export const SPACE_SHORT_FRAGMENT = gql(`
  fragment SpaceShort on Space {
    id
    name
    country
  }
`);

// Minimal fragment for ME_QUERY - only what's needed for dropdowns and basic UI
export const SPACE_FOR_DROPDOWN_FRAGMENT = gql(`
  fragment SpaceForDropdown on Space {
    id
    name
    country
    hasEpilo
    parent { id name country }
    children { id name country }
    activeSEMPlan { id semPlan { id plan label flags { multiTenancy } } }
  }
`);

// Minimal fragment for ME_QUERY - only what's needed for dropdowns
export const HOTEL_FOR_DROPDOWN_FRAGMENT = gql(`
  fragment HotelForDropdown on HotelListOutput {
    id
    name
    refCode
    space { id name country }
  }
`);

export const LIST_SPACES_PAGED_QUERY = gql(`
  query ListSpacesPaged($skip: Int, $take: Int, $search: String) {
    listSpacesPaged(skip: $skip, take: $take, search: $search) {
      totalCount
      spaces {
        space {
          ...SpaceForDropdown
        }
        hotels {
          ...HotelForDropdown
        }
      }
    }
  }
`);

export const EVICT_SPACES_QUERIES = (cache: ApolloCache<any>) => {
  EVICT_USERS_QUERIES(cache);
  cache.evict({ fieldName: 'listSpacesPaged' });
  cache.evict({ fieldName: 'listSpacesPagedHierarchical' });
  cache.gc();
};

export const REFETCH_SPACES_QUERIES = (spaceId?: number | undefined) => [...REFETCH_USERS_QUERIES()];

export const SEMPLANS_LIST_QUERY = gql(`
  query ListSEMPlans {
    listSEMPlans {
      ...SEMPlanFull
    }
  }
`);

export const SPACES_LIST_QUERY = gql(`
  query ListSpaces {
    listSpaces {
      ...SpaceFull
    }
  }
`);

export const SPACE_HIERARCHICAL_FRAGMENT = gql(`
  fragment SpaceHierarchical on SpaceHierarchicalOutput {
    level
    displayName
    space {
      ...SpaceFull
    }
  }
`);

export const SPACES_LIST_PAGED_HIERARCHICAL_QUERY = gql(`
  query ListSpacesPagedHierarchical($skip: Int, $take: Int, $search: String) {
    listSpacesPagedHierarchical(skip: $skip, take: $take, search: $search) {
      totalCount
      spaces {
        ...SpaceHierarchical
      }
    }
  }
`);

export const SPACE_VIEW_QUERY = gql(`
  query ViewSpace($id: Int!) {
    viewSpace(id: $id) {
      ...SpaceFull
    }
  }
`);

export const CREATE_SPACE_MUTATION = gql(`
  mutation ProvisionSpace($data: ProvisionSpaceInput!) {
    provisionSpace(data: $data) {
      space { ...SpaceShort }
      user { id name email username }
      initialPassword
    }
  }
`);

export const UPDATE_SPACE_MUTATION = gql(`
  mutation UpdateSpace($id: Int!, $data: SpaceUpdateInput!) {
    updateSpace(id: $id, data: $data) {
      ...SpaceFull
    }
  }
`);

export const DELETE_SPACE_MUTATION = gql(`
  mutation DeleteSpace($id: Int!) {
    deleteSpace(id: $id)
  }
`);

export const IMPORT_DEFAULTOFFERTEMPLATES_MUTATION = gql(`
  mutation ImportDefaultOfferTemplates($targetSpaceId: Int!) {
    importDefaultOfferTemplates(targetSpaceId: $targetSpaceId) {
      id name
    }
  }
`);

export const IMPORT_DEFAULTEMAILTEMPLATES_MUTATION = gql(`
  mutation ImportDefaultEmailTemplates($targetSpaceId: Int!) {
    importDefaultEmailTemplates(targetSpaceId: $targetSpaceId, overwrite: true) {
      id usage
    }
  }
`);

export const IMPORT_DEFAULTPRODUCTS_MUTATION = gql(`
  mutation ImportDefaultProducts($targetSpaceId: Int!, $sem: Boolean!, $epilo: Boolean!) {
    importDefaultProducts(targetSpaceId: $targetSpaceId, sem: $sem, epilo: $epilo)
  }
`);

export const IMPORT_DEFAULTPRICELISTS_MUTATION = gql(`
  mutation ImportDefaultPricelists($targetSpaceId: Int!, $sem: Boolean!, $epilo: Boolean!) {
    importDefaultPricelists(targetSpaceId: $targetSpaceId, sem: $sem, epilo: $epilo)
  }
`);

export const SPACE_SETTINGS_CI_QUERY = gql(`
  query QuerySpaceSettingsCi($spaceId: Int!) {
    querySpaceSettingsCi(spaceId: $spaceId) {
      spaceId
      logoFileName
      logoFileNameEnabled
      primaryColor
      primaryColorEnabled
      signature
      signatureEnabled
    }
  }
`);

export const UPDATE_SPACE_SETTINGS_CI_MUTATION = gql(`
  mutation UpdateSpaceSettingsCi($data: SpaceSettingsCiInput!) {
    updateSpaceSettingsCi(data: $data) {
      spaceId
      logoFileName
      logoFileNameEnabled
      primaryColor
      primaryColorEnabled
      signature
      signatureEnabled
    }
  }
`);

export const HOTEL_CHANGED_SUBSCRIPTION = gql(`
  subscription OnHotelChanged($hotelId: Int) {
    hotelChanged(hotelId: $hotelId) {
      id
      name
      refCode
    }
  }
`);

export const HOTEL_COMMISSION_RATES_QUERY = gql(`
  query ListHotelCommissionRates($hotelId: Int!) {
    listHotelCommissionRates(hotelId: $hotelId) {
      id
      customerType
      commissionBase
      commissionPercentage
      validFrom
      remark
      createdAt
    }
  }
`);

export const UPSERT_HOTEL_COMMISSION_RATE_MUTATION = gql(`
  mutation UpsertHotelCommissionRate($hotelId: Int!, $input: HotelCommissionRateInput!) {
    upsertHotelCommissionRate(hotelId: $hotelId, input: $input) {
      id
      customerType
      commissionBase
      commissionPercentage
      validFrom
      remark
      createdAt
    }
  }
`);

export const DELETE_HOTEL_COMMISSION_RATE_MUTATION = gql(`
  mutation DeleteHotelCommissionRate($id: Int!) {
    deleteHotelCommissionRate(id: $id)
  }
`);
